import { useState, useCallback } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { prepareAdvancementConfigForSave } from "../utils/advancementUtils";

function validateOvernightShiftClientSide(config) {
  if (!config.isOvernightShift) return null;
  if (!config.workStartTime || !config.workEndTime) {
    return "Overnight shifts require both Work Start Time and Work End Time";
  }
  const toMin = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
  };
  if (toMin(config.workEndTime) >= toMin(config.workStartTime)) {
    return "For overnight shifts, Work End Time must be earlier than Work Start Time (e.g. 14:00 → 02:00)";
  }
  if (
    config.shiftDayPivotHour !== null &&
    config.shiftDayPivotHour !== undefined &&
    config.shiftDayPivotHour !== ""
  ) {
    const p = Number(config.shiftDayPivotHour);
    if (!Number.isInteger(p) || p < 0 || p > 23) {
      return "Day-Boundary Hour must be an integer between 0 and 23";
    }
  }
  return null;
}

const DEFAULT_CONFIG = {
  companyName: "",
  attendanceRules: {},
  industry: "",
  email: "",
  phone: "",
  address: "",
  website: "",
  attendanceEffectiveDate: new Date().toISOString().split("T")[0],
  advancementEffectiveDate: new Date().toISOString().split("T")[0],
  overtimeEffectiveDate: new Date().toISOString().split("T")[0],
  leaveEffectiveDate: new Date().toISOString().split("T")[0],
  workStartTime: "09:00",
  workEndTime: "18:00",
  isOvernightShift: false,
  shiftDayPivotHour: null,
  workingHoursPerDay: 8,
  workingDaysPerWeek: 5,
  weekendDays: ["Saturday", "Sunday"],
  overtimeEnabled: true,
  overtimeRate: 1.5,
  overtimeStartAfter: 8,
  maxOvertimeHoursPerDay: 4,
  overtimeApprovalRequired: false,
  absenceDeductionPerDay: 0,
  defaultDailyRatePercentage: 0,
  lateDeductionRules: [
    { minutesFrom: 0, minutesTo: 15, deductionPercentage: 0 },
    { minutesFrom: 15, minutesTo: 30, deductionPercentage: 25 },
    { minutesFrom: 30, minutesTo: 60, deductionPercentage: 50 },
    { minutesFrom: 60, minutesTo: null, deductionPercentage: 100 },
  ],
  annualLeaveRules: [
    { yearsFrom: 0, yearsTo: 1, days: 15 },
    { yearsFrom: 1, yearsTo: 10, days: 21 },
    { yearsFrom: 10, yearsTo: null, days: 30 },
  ],
  sickLeavePerYear: 15,
  freeSickDays: 21,
  casualLeavePerYear: 7,
  leaveApprovalRequired: true,
  carryOverLeaves: false,
  performanceBonusEnabled: true,
  attendanceBonusEnabled: true,
  monthlyBonusAmount: 0,
  attendanceBonusCriteria: 100,
  lateNotification: true,
  absenceNotification: true,
  overtimeNotification: true,
  leaveNotification: true,
  privateMessaging: true,
  documentSharing: true,
  performanceReviews: true,
  salarySlips: true,
  advancementEnabled: true,
  maxAdvancementAmount: 10000,
  maxAdvancementPercentOfSalary: 50,
  minMonthsOfService: 3,
  maxActiveAdvancements: 1,
  maxAdvancementsPerMonth: null,
  maxMonthlyAdvancementTotal: null,
  defaultInstallments: 3,
  minInstallments: 1,
  maxInstallments: 12,
  requireManagerApproval: true,
  requireHRApproval: true,
  autoApproveUnderAmount: 0,
  advancementProcessingDays: 3,
  allowEmergencyAdvancements: true,
  emergencyMaxAmount: 5000,
  deductionStartDelay: 1,
  blockAdvancementIfPending: true,
  blockAdvancementIfOverdue: true,
  advancementNotifications: true,
  payrollStartDate: null,
  halfDayRequestThreshold: null,
  attendanceDetectionMode: "deviceName",
  // Working-company geofence areas. Declared here so the form always has a
  // defined shape: without it, selecting a company that has no configuration
  // yet would leave the previously selected company's locations in state and
  // carry them into the new config on save.
  allowCompanyDefaultLocation: true,
  managersCanImportAttendance: false,
  managersCanImportLocationSchedule: false,
  managersCanUseShiftControl: false,
  // Face check-in: OFF | OPTIONAL | REQUIRED (CompanyConfiguration.faceCheckInMode).
  faceCheckInMode: "OFF",
  defaultLocations: [],
};

const useConfiguration = ({ BASE_URL, profile }) => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  // Starts true (not false) so the page-level skeleton is guaranteed to be
  // visible on first paint instead of racing the mount effect that flips it.
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const [detectedCompanies, setDetectedCompanies] = useState([]);
  const [isCreatingNewConfig, setIsCreatingNewConfig] = useState(false);
  const [globalEffectiveDate, setGlobalEffectiveDate] = useState(null);
  const [unlockedTabs, setUnlockedTabs] = useState({
    attendance: false,
    overtime: false,
    leave: false,
    advancement: false,
  });

  const loadConfiguration = useCallback(
    async (companyName = null) => {
      try {
        setLoading(true);
        const clientName = profile?.clientName || localStorage.getItem("clientName");

        if (!companyName) {
          const detectResponse = await apiFetch(
            `${BASE_URL}/api/company-config/${clientName}/detect-companies`,
            {
              method: "GET",
              headers: { "Content-Type": "application/json" },
            }
          );
          if (detectResponse.ok) {
            const detectData = await detectResponse.json();
            if (
              detectData.success &&
              detectData.data.companies &&
              detectData.data.companies.length > 0
            ) {
              const companies = (detectData.data.companies || []).map((company) => ({
                ...company,
                isConfigured: company.hasConfiguration || company.isConfigured || false,
              }));
              setDetectedCompanies(companies);
              companyName = detectData.data.companies[0].companyName;
              setSelectedCompanyName(companyName);
            }
          }
        }

        if (!companyName) {
          console.log("No company name available");
          setLoading(false);
          return;
        }

        const response = await apiFetch(
          `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(companyName)}`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            const loadedConfig = {
              ...data.data,
              weekendDays: Array.isArray(data.data.weekendDays)
                ? data.data.weekendDays
                : data.data.weekendDays || ["Saturday", "Sunday"],
              annualLeaveRules: Array.isArray(data.data.annualLeaveRules)
                ? data.data.annualLeaveRules
                : data.data.annualLeaveRules || [
                    { yearsFrom: 0, yearsTo: 1, days: 15 },
                    { yearsFrom: 1, yearsTo: 10, days: 21 },
                    { yearsFrom: 10, yearsTo: null, days: 30 },
                  ],
              lateDeductionRules: Array.isArray(data.data.lateDeductionRules)
                ? data.data.lateDeductionRules
                : data.data.lateDeductionRules || [
                    { minutesFrom: 0, minutesTo: 15, deductionPercentage: 0 },
                    { minutesFrom: 15, minutesTo: 30, deductionPercentage: 25 },
                    { minutesFrom: 30, minutesTo: 60, deductionPercentage: 50 },
                    { minutesFrom: 60, minutesTo: null, deductionPercentage: 100 },
                  ],
              advancementEnabled: data.data.advancementEnabled ?? true,
              maxAdvancementAmount: data.data.maxAdvancementAmount ?? 10000,
              maxAdvancementPercentOfSalary: data.data.maxAdvancementPercentOfSalary ?? 50,
              minMonthsOfService: data.data.minMonthsOfService ?? 3,
              maxActiveAdvancements: data.data.maxActiveAdvancements ?? 1,
              maxAdvancementsPerMonth: data.data.maxAdvancementsPerMonth ?? null,
              maxMonthlyAdvancementTotal: data.data.maxMonthlyAdvancementTotal ?? null,
              defaultInstallments: data.data.defaultInstallments ?? 3,
              minInstallments: data.data.minInstallments ?? 1,
              maxInstallments: data.data.maxInstallments ?? 12,
              requireManagerApproval: data.data.requireManagerApproval ?? true,
              requireHRApproval: data.data.requireHRApproval ?? true,
              autoApproveUnderAmount: data.data.autoApproveUnderAmount ?? 0,
              advancementProcessingDays: data.data.advancementProcessingDays ?? 3,
              allowEmergencyAdvancements: data.data.allowEmergencyAdvancements ?? true,
              emergencyMaxAmount: data.data.emergencyMaxAmount ?? 5000,
              deductionStartDelay: data.data.deductionStartDelay ?? 1,
              blockAdvancementIfPending: data.data.blockAdvancementIfPending ?? true,
              blockAdvancementIfOverdue: data.data.blockAdvancementIfOverdue ?? true,
              advancementNotifications: data.data.advancementNotifications ?? true,
              halfDayRequestThreshold: data.data.halfDayRequestThreshold ?? null,
              freeSickDays: data.data.freeSickDays ?? 21,
              isOvernightShift: data.data.isOvernightShift ?? false,
              shiftDayPivotHour: data.data.shiftDayPivotHour ?? null,
              department: data.data.department || "",
              workingCompany: data.data.workingCompany || "",
              attendanceEffectiveDate: data.data.attendanceEffectiveDate
                ? new Date(data.data.attendanceEffectiveDate).toISOString().split("T")[0]
                : new Date().toISOString().split("T")[0],
              overtimeEffectiveDate: data.data.overtimeEffectiveDate
                ? new Date(data.data.overtimeEffectiveDate).toISOString().split("T")[0]
                : new Date().toISOString().split("T")[0],
              leaveEffectiveDate: data.data.leaveEffectiveDate
                ? new Date(data.data.leaveEffectiveDate).toISOString().split("T")[0]
                : new Date().toISOString().split("T")[0],
              advancementEffectiveDate: data.data.advancementEffectiveDate
                ? new Date(data.data.advancementEffectiveDate).toISOString().split("T")[0]
                : new Date().toISOString().split("T")[0],
              payrollStartDate: data.data.payrollStartDate ?? null,
              attendanceDetectionMode: data.data.attendanceDetectionMode || "deviceName",
              // Normalised explicitly rather than relying on the spread: the
              // column is NULL when no areas are configured, and the editor
              // needs an array to map over. Always assigned, so switching
              // companies can never leave the previous one's areas behind.
              defaultLocations: Array.isArray(data.data.defaultLocations)
                ? data.data.defaultLocations
                : [],
              allowCompanyDefaultLocation: data.data.allowCompanyDefaultLocation ?? true,
              managersCanImportAttendance: data.data.managersCanImportAttendance ?? false,
              managersCanImportLocationSchedule: data.data.managersCanImportLocationSchedule ?? false,
              managersCanUseShiftControl: data.data.managersCanUseShiftControl ?? false,
              faceCheckInMode: data.data.faceCheckInMode ?? "OFF",
              // Superadmin's Biometric Integration switch for this working company
              // (read-only; false hides the Face Check-in selector).
              faceCheckInAvailable: data.data.faceCheckInAvailable ?? true,
            };

            console.log("✅ Loaded advancement config:", {
              enabled: loadedConfig.advancementEnabled,
              maxAmount: loadedConfig.maxAdvancementAmount,
              maxPercent: loadedConfig.maxAdvancementPercentOfSalary,
            });

            setConfig((prevConfig) => ({ ...prevConfig, ...loadedConfig }));
            setSelectedCompanyName(companyName);
            setIsCreatingNewConfig(false);
          } else {
            // Company has no configuration yet. Config state is merged, not
            // replaced, so anything left from the previously selected company
            // would be saved onto this one. Clear the working-company location
            // fields explicitly — they are scoped to a single company and must
            // never cross over.
            setConfig((prevConfig) => ({
              ...prevConfig,
              defaultLocations: [],
              allowCompanyDefaultLocation: true,
              managersCanImportAttendance: false,
              managersCanImportLocationSchedule: false,
              managersCanUseShiftControl: false,
              faceCheckInMode: "OFF",
            }));
          }
        }
      } catch (error) {
        console.error("Error loading configuration:", error);
      } finally {
        setLoading(false);
      }
    },
    [BASE_URL, profile]
  );

  const persistConfiguration = async ({
    createNewVersion = false,
    isEditingArchive,
    selectedArchiveId,
    updateArchive,
    showAlert,
    loadArchives,
    isRTL,
    t,
  } = {}) => {
    if (!selectedCompanyName) {
      showAlert(
        isRTL ? "تنبيه" : "Warning",
        isRTL
          ? 'يرجى اختيار شركة أولاً باستخدام زر "اختر الشركة"'
          : 'Please select a company first using the "Select Company" button',
        "warning"
      );
      return;
    }

    // Validate overnight shift — use department-specific values if a department is selected
    const configToValidate = (() => {
      if (config.department && config.departmentRules && config.departmentRules[config.department]) {
        const deptRules = config.departmentRules[config.department];
        return {
          isOvernightShift: deptRules.isOvernightShift ?? config.isOvernightShift,
          workStartTime: deptRules.workStartTime ?? config.workStartTime,
          workEndTime: deptRules.workEndTime ?? config.workEndTime,
          shiftDayPivotHour: deptRules.shiftDayPivotHour ?? config.shiftDayPivotHour,
        };
      }
      return config;
    })();
    const overnightError = validateOvernightShiftClientSide(configToValidate);
    if (overnightError) {
      showAlert(isRTL ? "خطأ في الإعدادات" : "Invalid configuration", overnightError, "error");
      return;
    }

    try {
      if (isEditingArchive && selectedArchiveId) {
        await updateArchive(selectedArchiveId);
        return;
      }
      setLoading(true);
      const clientName = profile?.clientName || localStorage.getItem("clientName");

      const {
        companyName,
        industry,
        email,
        phone,
        address,
        website,
        department,
        departmentRules,
        ...editableConfig
      } = config;

      const advancementConfig = prepareAdvancementConfigForSave(editableConfig);
      const {
        advancementEnabled: _ae,
        maxAdvancementAmount: _maa,
        maxAdvancementPercentOfSalary: _maps,
        minMonthsOfService: _mmos,
        maxActiveAdvancements: _maa2,
        maxAdvancementsPerMonth: _mapm,
        maxMonthlyAdvancementTotal: _mmat,
        defaultInstallments: _di,
        minInstallments: _mini,
        maxInstallments: _maxi,
        requireManagerApproval: _rma,
        requireHRApproval: _rha,
        autoApproveUnderAmount: _aaua,
        advancementProcessingDays: _apd,
        allowEmergencyAdvancements: _aea,
        emergencyMaxAmount: _ema,
        deductionStartDelay: _dsd,
        blockAdvancementIfPending: _baip,
        blockAdvancementIfOverdue: _baio,
        advancementNotifications: _an,
        ...otherConfig
      } = editableConfig;

      const COMPANY_WIDE_ATTENDANCE_FIELDS = [
        "workStartTime",
        "workEndTime",
        "isOvernightShift",
        "shiftDayPivotHour",
        "workingHoursPerDay",
        "workingDaysPerWeek",
        "weekendDays",
        "halfDayRequestThreshold",
        "overtimeEnabled",
        "overtimeRate",
        "overtimeStartAfter",
        "maxOvertimeHoursPerDay",
        "absenceDeductionPerDay",
        "defaultDailyRatePercentage",
        "lateDeductionRules",
        "allowDefaultLocation",
        // Working-company scoped, with no per-department override: a save made
        // while a department is selected must not carry them in the payload.
        "allowCompanyDefaultLocation",
        "defaultLocations",
        "managersCanImportAttendance",
        "managersCanImportLocationSchedule",
        "managersCanUseShiftControl",
        "faceCheckInMode",
      ];

      let companyPayload = { ...otherConfig };
      delete companyPayload.faceCheckInAvailable; // read-only hint from the server
      if (department) {
        COMPANY_WIDE_ATTENDANCE_FIELDS.forEach((f) => delete companyPayload[f]);
      }

      const payload = {
        clientName,
        companyName: selectedCompanyName,
        createNewVersion,
        archiveReason: createNewVersion ? "manual_create_version" : undefined,
        ...companyPayload,
        ...advancementConfig,
      };

      // Include departmentRules as attendanceRules if they exist
      if (departmentRules && typeof departmentRules === "object" && Object.keys(departmentRules).length > 0) {
        payload.attendanceRules = departmentRules;
      }

      console.log("📤 Sending company config:", payload);

      const response = await apiFetch(`${BASE_URL}/api/company-config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!response.ok || !responseData.success) {
        throw new Error(responseData.message || "Failed to save company config");
      }

      if (department) {
        console.log(`📤 Saving department rules for: ${department}`);

        // Read department-specific values from departmentRules, falling back to company-wide
        const deptSpecificRules = (departmentRules && departmentRules[department]) || {};

        const deptRulesPayload = {
          workStartTime: deptSpecificRules.workStartTime ?? editableConfig.workStartTime,
          workEndTime: deptSpecificRules.workEndTime ?? editableConfig.workEndTime,
          isOvernightShift: Boolean(deptSpecificRules.isOvernightShift ?? editableConfig.isOvernightShift),
          shiftDayPivotHour: (() => {
            const val = deptSpecificRules.shiftDayPivotHour ?? editableConfig.shiftDayPivotHour;
            return val === "" || val === null || val === undefined ? null : Number(val);
          })(),
          workingHoursPerDay: deptSpecificRules.workingHoursPerDay ?? editableConfig.workingHoursPerDay,
          workingDaysPerWeek: deptSpecificRules.workingDaysPerWeek ?? editableConfig.workingDaysPerWeek,
          weekendDays: deptSpecificRules.weekendDays ?? editableConfig.weekendDays,
          lateDeductionRules: deptSpecificRules.lateDeductionRules ?? editableConfig.lateDeductionRules,
          absenceDeductionPerDay: deptSpecificRules.absenceDeductionPerDay ?? editableConfig.absenceDeductionPerDay,
          overtimeEnabled: deptSpecificRules.overtimeEnabled ?? editableConfig.overtimeEnabled,
          overtimeRate: deptSpecificRules.overtimeRate ?? editableConfig.overtimeRate,
          overtimeStartAfter: deptSpecificRules.overtimeStartAfter ?? editableConfig.overtimeStartAfter,
          maxOvertimeHoursPerDay: deptSpecificRules.maxOvertimeHoursPerDay ?? editableConfig.maxOvertimeHoursPerDay,
          maxLatePerMonth: deptSpecificRules.maxLatePerMonth ?? editableConfig.maxLatePerMonth,
          gracePeriodsPerMonth: deptSpecificRules.gracePeriodsPerMonth ?? editableConfig.gracePeriodsPerMonth,
          halfDayRequestThreshold: deptSpecificRules.halfDayRequestThreshold ?? editableConfig.halfDayRequestThreshold,
          allowDefaultLocation: deptSpecificRules.allowDefaultLocation ?? editableConfig.allowDefaultLocation ?? true,
        };

        const deptResponse = await apiFetch(
          `${BASE_URL}/api/company-config/${clientName}/${encodeURIComponent(
            selectedCompanyName
          )}/departments/${encodeURIComponent(department)}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(deptRulesPayload),
          }
        );

        const deptData = await deptResponse.json();

        if (deptResponse.ok && deptData.success) {
          console.log(`✅ Department rules saved for: ${department}`);
        } else {
          console.warn(`⚠️ Failed to save department rules: ${deptData.message}`);
        }
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);

      if (createNewVersion) {
        showAlert(
          isRTL ? "نجاح" : "Success",
          isRTL
            ? "تم إنشاء إصدار إعدادات جديد وأرشفة الإصدار السابق"
            : "New configuration version created and previous version archived",
          "success"
        );
      }

      await loadConfiguration(selectedCompanyName);
      await loadArchives(selectedCompanyName);
    } catch (error) {
      console.error("Error saving configuration:", error);
      showAlert(
        isRTL ? "خطأ" : "Error",
        (isRTL ? "فشل حفظ الإعدادات: " : "Failed to save configuration: ") + error.message,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGlobalDateChange = (date) => {
    setGlobalEffectiveDate(date);
    setConfig((prev) => {
      const updates = {};
      if (!unlockedTabs.attendance) updates.attendanceEffectiveDate = date;
      if (!unlockedTabs.overtime) updates.overtimeEffectiveDate = date;
      if (!unlockedTabs.leave) updates.leaveEffectiveDate = date;
      if (!unlockedTabs.advancement) updates.advancementEffectiveDate = date;
      return { ...prev, ...updates };
    });
  };

  const unlockTab = (tabKey) => {
    setUnlockedTabs((prev) => ({ ...prev, [tabKey]: true }));
  };

  const resetTabToGlobal = (tabKey) => {
    setUnlockedTabs((prev) => ({ ...prev, [tabKey]: false }));
    if (globalEffectiveDate) {
      setConfig((prev) => ({
        ...prev,
        [`${tabKey}EffectiveDate`]: globalEffectiveDate,
      }));
    }
  };

  const getTabEffectiveDate = (tabKey) => {
    if (unlockedTabs[tabKey]) {
      return config[`${tabKey}EffectiveDate`];
    }
    return globalEffectiveDate || config[`${tabKey}EffectiveDate`];
  };

  const getBadgeMode = (tabKey) => {
    if (!isCreatingNewConfig) return "locked";
    if (unlockedTabs[tabKey]) return "custom";
    return "synced";
  };

  return {
    config,
    setConfig,
    loading,
    setLoading,
    saveSuccess,
    selectedCompanyName,
    setSelectedCompanyName,
    detectedCompanies,
    setDetectedCompanies,
    isCreatingNewConfig,
    setIsCreatingNewConfig,
    loadConfiguration,
    persistConfiguration,
    globalEffectiveDate,
    setGlobalEffectiveDate,
    unlockedTabs,
    setUnlockedTabs,
    handleGlobalDateChange,
    unlockTab,
    resetTabToGlobal,
    getTabEffectiveDate,
    getBadgeMode,
  };
};

export default useConfiguration;